import { ApPayee, Props as ApPayeeProps } from '@/components/ApPayee';
import { useGetPopupContainer } from '@/components/ReportForm/Container/PopupContainer';
import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import {
  InputBaseType,
  SelectType,
} from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import { FormSelectProps } from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

export const instantPayeeActivatedValue = '1006';

export type Props<TFieldValues extends FieldValues> =
  InputBaseType<TFieldValues> & {
    inputRef?: ApPayeeProps<TFieldValues>['inputRef'];
    instantPayeeActivated?: boolean;
    formContext?: ApPayeeProps<TFieldValues>['formContext'];
    onClear?: VoidFunction;
  } & SelectType &
    Pick<ApPayeeProps<TFieldValues>, 'onChange' | 'onChangeAfter'>;

const InnerApReportFormInputPayeeSelect = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  label,
  tooltip,
  caption,
  inputRef,
  hidden,
  defaultOptions,
  instantPayeeActivated = true,
  required = false,
  onClear,
  ...rest
}: Props<TFieldValues>) => {
  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const { t } = useTranslation();
  const rules: { required: string | boolean } = useMemo(
    () => ({ required: required ? t('required') : false }),
    [required, t]
  );
  const containerRef = useGetPopupContainer();
  const memorizedSelectProps = useMemo(
    (): Omit<
      FormSelectProps<TFieldValues>['selectProps'],
      'options' | 'comboBox'
    > => ({
      getPopupContainer: containerRef,
      onClear,
    }),
    [containerRef, onClear]
  );
  return (
    <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
      <ApPayee<TFieldValues>
        control={control}
        name={name}
        inputId={inputId}
        instantPayeeActivatedValue={
          instantPayeeActivated ? instantPayeeActivatedValue : undefined
        }
        useDisplayName
        rules={rules}
        inputRef={inputRef}
        selectProps={memorizedSelectProps}
        defaultOptions={defaultOptions}
        include_inactive={false}
        {...rest}
      />
    </InputBase>
  );
};

InnerApReportFormInputPayeeSelect.displayName =
  'InnerApReportFormInputPayeeSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputPayeeSelect = memo((props) => (
  <InnerApReportFormInputPayeeSelect {...props} />
)) as typeof InnerApReportFormInputPayeeSelect;
ApReportFormInputPayeeSelect.displayName = 'ApReportFormInputPayeeSelect';
